import { ClockIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import cloneDeep from 'clone-deep'
import { useMemo, useState } from 'react'
import { Tooltip } from 'stories/components'
import type { Color } from 'stories/types'
import { getCharFromIndex } from 'utils'

import { Checkbox } from '../Checkbox/Checkbox'

interface MultiSelectProps {
  /**
   * What background color to use
   */
  color?: Color
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is readOnly
   */
  readOnly?: boolean
  /**
   * Tooltip of Input
   */
  tooltip?: string
  /**
   * Id of Input
   */
  id: string
  /**
   * Title of Input
   */
  title?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: string[] | Record<string, boolean> | undefined
  /**
   * Error of Input
   */
  error?: string
  /**
   * Options of select
   */
  options?: Array<string> | Record<string, string> // (Title => key)
  /**
   * Custom class name
   */
  className?: string
  /**
   * Required
   */
  required?: boolean
  /**
   * Has default option - 'SELECT'
   */
  hasDefaultOption?: boolean
  /**
   * Default option text
   */
  defaultOptionText?: string
  /**
   * Optional history handler
   */
  /**
   * Prefix
   */
  prefix?: string
  /**
   * Show History
   */
  history?: boolean

  sort?: boolean

  defaultSelected?: boolean

  additionalElements?: JSX.Element | Function | null

  sortKey?: 'key' | 'value'
  /**
   * Optional click handler
   */
  onChange?: (value: any) => void
  onBlur?: () => void
  showHistory?: () => void
  /** Allow typing values not present in options (e.g. external email addresses). */
  allowCustomValues?: boolean
  customValuePlaceholder?: string
  customValueValidator?: (value: string) => boolean
}

/**
 * Primary UI component for user interaction
 */
export const MultiSelect = ({
  disabled = false,
  readOnly = false,
  tooltip = '',
  title = '',
  value = {},
  error = '',
  options = [],
  className = '',
  prefix = '',
  required = false,
  history = false,
  sort = false,
  defaultSelected = true,
  additionalElements = null,
  sortKey = 'value',
  onChange = () => {},
  onBlur = () => {},
  showHistory = () => {},
  allowCustomValues = false,
  customValuePlaceholder = 'Add custom value',
  customValueValidator,
}: MultiSelectProps) => {
  const [isShowDropDown, setShowDropDown] = useState(false)
  const [lastValue, setLastValue] = useState<string[] | Record<string, boolean>>(Array.isArray(value) ? [] : {})
  const [customDraft, setCustomDraft] = useState('')
  const [customError, setCustomError] = useState('')

  const filter = (value: string[] | Record<string, boolean>, key: string) => {
    return Array.isArray(value) ? value.includes(key) : value[key]
  }

  const onVisibleDropDown = (isOpen: boolean) => {
    setShowDropDown(isOpen)
    if (isShowDropDown != isOpen && onBlur && !isOpen) {
      // const curVal = Array.isArray(value)
      //   ? value
      //   : Object.keys(value)
      //       .filter((key) => [true, undefined].includes((value as any)[key]))
      //       .sort()
      // const lastVal = Array.isArray(lastValue)
      //   ? lastValue
      //   : Object.keys(lastValue)
      //       .filter((key) => [true, undefined].includes((lastValue as any)[key]))
      //       .sort()
      if (JSON.stringify(value) != JSON.stringify(lastValue)) onBlur()
    }
    if (isOpen) setLastValue(cloneDeep(value))
  }

  const classNames = [
    'block',
    'rounded-xl',
    'px-2.5',
    'pr-8',
    'pb-[2px]',
    title ? 'pt-[27px]' : 'pt-[4px]',
    'w-full',
    'text-[15px]',
    'text-gray-900',
    'disabled:opacity-100',
    disabled ? 'bg-slate-100' : 'bg-white',
    readOnly || disabled ? 'cursor-not-allowed' : 'cursor-pointer',
    'border',
    'border-slate-300',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-shade-blue/20',
    'focus:bg-white',
    'transition',
    'hover:border-slate-400',
    'focus:border-shade-blue',
    'peer',
    'text-left',
    'relative',
    error && 'border-rose-700',
  ]
  if (value === null) value = {}

  if (Array.isArray(options) && options.length > 0 && typeof options[0] === 'object' && 'key' in options[0]) {
    const newOptions: Record<string, string> = {}
    ;(options as any[])
      .filter((v) => v.isVisible !== false)
      .forEach((option: { key: string; title: string }) => (newOptions[option.key] = option.title))
    options = newOptions as Record<string, string>
  }

  const isArray = Array.isArray(options)
  let keys = isArray ? (options as string[]) : (Object.keys(options) as string[])
  if (sort) {
    if (sortKey === 'value' && !isArray)
      keys = keys.sort((a, b) => ((options as any)[a] > (options as any)[b] ? 1 : -1))
    else keys = keys.sort()
  }

  const isAllSelected = useMemo(() => keys.filter((key) => filter(value, key)).length == keys.length, [value, keys])

  const customValues = useMemo(() => {
    if (!allowCustomValues || !Array.isArray(value)) return []
    return value.filter((item) => !keys.includes(item))
  }, [allowCustomValues, value, keys])

  const displayText = useMemo(() => {
    const selectedOptionLabels = keys
      .filter((key) => filter(value, key) || (defaultSelected && !Array.isArray(value) && value[key] === undefined))
      .map((key) => (isArray ? key : (options as any)[key]))
    return [...selectedOptionLabels, ...customValues].join(', ') || ' '
  }, [customValues, defaultSelected, isArray, keys, options, value])

  const addCustomValue = () => {
    const trimmed = customDraft.trim().toLowerCase()
    if (!trimmed) return
    if (customValueValidator && !customValueValidator(trimmed)) {
      setCustomError('Enter a valid email address')
      return
    }
    const current = Array.isArray(value) ? value : []
    if (current.includes(trimmed)) {
      setCustomDraft('')
      setCustomError('')
      return
    }
    onChange([...current, trimmed].sort())
    setCustomDraft('')
    setCustomError('')
  }

  const removeCustomValue = (customValue: string) => {
    if (!Array.isArray(value)) return
    onChange(value.filter((item) => item !== customValue))
  }

  const onChangeValue = (key: string, v: boolean) => {
    let newValue = cloneDeep(value)
    if ((value as any) === '') newValue = {}
    if (Array.isArray(newValue)) {
      if (v) newValue.push(key)
      else newValue.splice(newValue.indexOf(key), 1)
      newValue.sort()
    } else newValue[key] = v
    onChange(newValue)
  }

  const onSelectAll = () => {
    const newValue = cloneDeep(value)

    if (Array.isArray(newValue)) {
      const newValue = isAllSelected ? [] : keys
      onChange(newValue)
      return
    } else keys.map((key) => (newValue[key] = !isAllSelected))

    onChange(newValue)
  }

  const getPrefix = (index: number) => {
    if (prefix == 'alphabet') return `${getCharFromIndex(index)}. `
    if (prefix == 'number') return `${index + 1}. `
    return ''
  }

  return (
    <div className="input-container">
      <div className={`group relative w-full group ${isShowDropDown ? 'z-[51]' : ''} ${className}`}>
        {title && (
          <div className="absolute text-[12px] text-gray-700 top-1.5 z-10 origin-[0] left-2.5 flex gap-2 items-center max-w-[calc(100%-2rem)]">
            <span className="truncate" title={title}>
              {title}
            </span>
            {required && '*'}
            {tooltip.length > 0 ? <Tooltip message={tooltip}></Tooltip> : null}
            {additionalElements !== null &&
              (typeof additionalElements == 'function' ? additionalElements(onChange) : additionalElements)}
            {history && (
              <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
                <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          className={classNames.join(' ')}
          disabled={disabled || readOnly}
          onClick={() => {
            if (disabled || readOnly) return
            onVisibleDropDown(!isShowDropDown)
          }}
        >
          <p className="whitespace-nowrap text-ellipsis overflow-hidden h-[23px]">{displayText}</p>
          <ChevronDownIcon
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-900 pointer-events-none transition-transform ${
              isShowDropDown ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>
        {isShowDropDown && (
          <div
            className="absolute shadow bg-white p-2 border w-full z-40"
            onMouseLeave={() => onVisibleDropDown(false)}
          >
            <button className="text-shade-blue underline text-sm ml-1 mb-1" onClick={onSelectAll}>
              {isAllSelected ? 'Unselect All' : 'Select All'}
            </button>
            <div className="max-h-72 overflow-y-auto">
              {keys.map((key, index) => (
                <div key={key} className="ml-1">
                  <Checkbox
                    title={`${getPrefix(index)}${isArray ? key : (options as any)[key]}`}
                    id={key}
                    value={filter(value, key) || (defaultSelected && !Array.isArray(value) && value[key] === undefined)}
                    onChange={(value) => onChangeValue(key, value)}
                  />
                </div>
              ))}
              {allowCustomValues &&
                customValues.map((customValue) => (
                  <div key={customValue} className="ml-1 flex items-center justify-between gap-2 pr-1">
                    <span className="text-sm text-gray-900 truncate">{customValue}</span>
                    <button
                      type="button"
                      className="text-red-700 text-xs hover:underline shrink-0"
                      onClick={() => removeCustomValue(customValue)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              {allowCustomValues && (
                <div className="mt-2 border-t pt-2">
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={customDraft}
                      placeholder={customValuePlaceholder}
                      className="flex-1 min-w-0 rounded border border-slate-300 px-2 py-1 text-sm"
                      onChange={(event) => {
                        setCustomDraft(event.target.value)
                        if (customError) setCustomError('')
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          addCustomValue()
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="text-shade-blue text-sm px-2 hover:underline shrink-0"
                      onClick={addCustomValue}
                    >
                      Add
                    </button>
                  </div>
                  {customError && <p className="text-rose-700 text-xs pt-1">{customError}</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <p className="peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1">{error}</p>}
    </div>
  )
}
